package com.yogaaicoach

import com.yogaaicoach.pages.auth.CreateAccountPage
import com.yogaaicoach.pages.auth.EmailSignInPage
import com.yogaaicoach.pages.auth.ForgotPasswordPage
import com.yogaaicoach.pages.auth.LoginPage
import com.yogaaicoach.pages.auth.ResetPasswordConfirmationPage
import com.yogaaicoach.pages.auth.TokenDataManager
import com.yogaaicoach.pages.auth.VerifyEmailPage
import com.yogaaicoach.common.analytics.trackRouteView
import com.yogaaicoach.common.ui.components.LoadingPage
import com.yogaaicoach.pages.dashboard.DashboardPage
import com.yogaaicoach.pages.contact.ContactUsPage
import com.yogaaicoach.pages.insights.InsightsPage
import com.yogaaicoach.pages.payment.ManageSubscriptionPage
import com.yogaaicoach.pages.payment.SubscriptionPage
import com.yogaaicoach.pages.trainingprogram.AddNewTrainingFlowPage
import com.yogaaicoach.pages.trainingprogram.AddTraining1Hash
import com.yogaaicoach.pages.trainingprogram.AddTrainingExistingHash
import com.yogaaicoach.pages.trainingprogram.UpdateTrainingFlowPage
import com.yogaaicoach.pages.trainingprogram.UpdateTrainingHash
import com.yogaaicoach.pages.workout.EditWorkoutPage
import com.yogaaicoach.pages.workout.WorkoutOverviewPage
import com.yogaaicoach.pages.workout.WorkoutPage
import com.yogaaicoach.pages.workout.test.TestExercisePage
import com.yogaaicoach.pages.workoutresults.WorkoutResultsPage
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import react.FC
import react.Props
import react.useEffect
import react.useEffectOnce
import react.useState
import web.events.EventHandler
import web.history.history
import web.location.location
import web.window.window

private const val EmailSignInHash = "#email-signin"
private const val CreateAccountHash = "#create-account"
private const val ForgotPasswordHash = "#forgot-password"
private const val VerifyEmailHash = "#verify-email"
private const val ResetConfirmationHash = "#reset-confirmation"
private const val AddTrainingPrefix = "#add-training"
private const val UpdateTrainingPrefix = "#update-training"
private const val InsightsHash = "#insights"
private const val UpgradeHash = "#upgrade"
private const val SubscriptionHash = "#subscription"
private const val ContactUsHash = "#contact-us"
private const val WorkoutOverviewHash = "#workout"
private const val WorkoutSessionHash = "#workout-session"
private const val WorkoutResultsHash = "#workout-results"
private const val EditWorkoutHash = "#edit-workout"
private const val TestExerciseHash = "#test-exercise"

// Avoids a spinner flash for loads that resolve almost instantly; below this threshold the
// screen just stays blank for a beat, which reads as "fast" rather than "broken".
private const val LoadingSpinnerDelayMs = 200L

internal val AppRoot = FC<Props> {
    var isLoading by useState(true)
    var showLoadingSpinner by useState(false)
    var isLoggedIn by useState(false)
    var pendingWorkoutGuid by useState("")
    var pendingWorkoutDuration by useState(0)
    var pendingWorkoutIsCompleted by useState(false)
    var pendingStartExerciseNumber by useState(1)

    // Detect Stripe redirect params on first render (before useEffectOnce cleans the URL).
    val initialSearch = location.search
    var showStripeSuccess by useState("stripe=success" in initialSearch)
    var showStripeCancel by useState("stripe=cancel" in initialSearch)

    // If this is a Stripe redirect, start at the right page instead of location.hash (which is "").
    var route by useState(
        when {
            "stripe=success" in initialSearch -> SubscriptionHash
            "stripe=cancel" in initialSearch -> UpgradeHash
            else -> location.hash
        }
    )
    var pendingEmail by useState("")

    fun navigateTo(hash: String) {
        location.hash = hash.removePrefix("#")
    }

    // Same as Navigation.kt's Dashboard?isReset=true. dashboardViewModel is a long-lived
    // singleton here, so its loadPage() no-ops once isPageLoaded is true — returning from a
    // completed workout would otherwise show the totals from before the workout.
    fun navigateToDashboardReloading() {
        dashboardViewModel.loadPage(sharedAppViewModel::askNewToken, isReload = true)
        navigateTo("")
    }

    fun navigateToWorkout(guid: String, duration: Int, isCompleted: Boolean) {
        pendingWorkoutGuid = guid
        pendingWorkoutDuration = duration
        pendingWorkoutIsCompleted = isCompleted
        workoutOverviewViewModel.reset()
        navigateTo(WorkoutOverviewHash)
    }

    @OptIn(kotlin.js.ExperimentalWasmJsInterop::class)
    fun navigateReplacing(hash: String) {
        history.replaceState(null, "", "#${hash.removePrefix("#")}")
        route = location.hash
    }

    useEffectOnce {
        // Clean ?stripe=... query params from the URL now that we've captured them in state.
        if (showStripeSuccess || showStripeCancel) {
            @OptIn(kotlin.js.ExperimentalWasmJsInterop::class)
            history.replaceState(null, "", "./")
        }

        scope.launch { sharedAppViewModel.isLoading.collect { isLoading = it } }
        scope.launch {
            sharedAppViewModel.isLoggedIn.collect {
                if (it) dashboardViewModel.reset()
                if (it) insightsViewModel.reset()
                if (it) manageSubscriptionViewModel.reset()
                if (it) subscriptionViewModel.reset()
                if (it) workoutOverviewViewModel.reset()
                isLoggedIn = it
                // Navigate to dashboard on login, unless Stripe redirected us to a specific page.
                if (it && !showStripeSuccess && !showStripeCancel) navigateTo("")
            }
        }
        window.onhashchange = EventHandler { route = location.hash }
    }

    useEffect(isLoading) {
        if (isLoading) {
            delay(LoadingSpinnerDelayMs)
            showLoadingSpinner = true
        } else {
            showLoadingSpinner = false
        }
    }

    // Gated on !isLoading so the initial render doesn't report a "Sign In" view for a session
    // that is about to resolve as logged in.
    useEffect(route, isLoggedIn, isLoading) {
        if (!isLoading) trackRouteView(route, isLoggedIn)
    }

    fun paymentNavigationTarget(): String =
        if (TokenDataManager.tokenData?.isSubscribed == true) SubscriptionHash else UpgradeHash

    when {
        isLoading -> if (showLoadingSpinner) LoadingPage {}
        isLoggedIn && route.startsWith(UpdateTrainingPrefix) -> UpdateTrainingFlowPage {
            viewModel = updateTrainingViewModel
            appViewModel = sharedAppViewModel
            this.route = route
            this.navigateTo = ::navigateTo
            this.navigateReplacing = ::navigateReplacing
            onNavigateToPayment = { navigateTo(paymentNavigationTarget()) }
            onNavigateToContactUs = { navigateTo(ContactUsHash) }
        }
        isLoggedIn && route.startsWith(AddTrainingPrefix) -> AddNewTrainingFlowPage {
            viewModel = addNewTrainingViewModel
            appViewModel = sharedAppViewModel
            this.route = route
            this.navigateTo = ::navigateTo
            this.navigateReplacing = ::navigateReplacing
            onNavigateToPayment = { navigateTo(paymentNavigationTarget()) }
            onNavigateToContactUs = { navigateTo(ContactUsHash) }
        }
        isLoggedIn && route == InsightsHash -> InsightsPage {
            viewModel = insightsViewModel
            appViewModel = sharedAppViewModel
            onNavigateToDashboard = { navigateTo("") }
            onNavigateToPayment = { navigateTo(paymentNavigationTarget()) }
            onNavigateToContactUs = { navigateTo(ContactUsHash) }
        }
        isLoggedIn && route == SubscriptionHash -> ManageSubscriptionPage {
            viewModel = manageSubscriptionViewModel
            appViewModel = sharedAppViewModel
            showSuccessMessage = showStripeSuccess
            onNoSubscriptionFound = { navigateTo(UpgradeHash) }
            onNavigateToDashboard = { navigateTo("") }
            onNavigateToPayment = { navigateTo(paymentNavigationTarget()) }
            onNavigateToContactUs = { navigateTo(ContactUsHash) }
        }
        isLoggedIn && route == UpgradeHash -> SubscriptionPage {
            viewModel = subscriptionViewModel
            appViewModel = sharedAppViewModel
            isCancel = showStripeCancel
            onNavigateToDashboard = { navigateTo("") }
            onNavigateToPayment = { navigateTo(paymentNavigationTarget()) }
            onNavigateToContactUs = { navigateTo(ContactUsHash) }
        }
        isLoggedIn && route == WorkoutSessionHash -> WorkoutPage {
            viewModel = workoutScreenViewModel
            appViewModel = sharedAppViewModel
            workoutGuid = pendingWorkoutGuid
            startExerciseNumber = pendingStartExerciseNumber
            onWorkoutCompleted = { workoutResultsViewModel.reset(); navigateTo(WorkoutResultsHash) }
            onNavigateBack = { workoutScreenViewModel.reset(); navigateTo(WorkoutOverviewHash) }
            onNavigateToDashboard = { navigateTo("") }
            onNavigateToPayment = { navigateTo(paymentNavigationTarget()) }
            onNavigateToContactUs = { navigateTo(ContactUsHash) }
        }
        isLoggedIn && route == WorkoutResultsHash -> WorkoutResultsPage {
            viewModel = workoutResultsViewModel
            appViewModel = sharedAppViewModel
            workoutGuid = pendingWorkoutGuid
            onNavigateToDashboard = { navigateToDashboardReloading() }
            onNavigateToPayment = { navigateTo(paymentNavigationTarget()) }
            onNavigateToContactUs = { navigateTo(ContactUsHash) }
        }
        isLoggedIn && route == EditWorkoutHash -> EditWorkoutPage {
            viewModel = editWorkoutViewModel
            appViewModel = sharedAppViewModel
            workoutGuid = pendingWorkoutGuid
            defaultDuration = pendingWorkoutDuration
            navigateTo = ::navigateTo
            onSaveDone = { _, durationMinutes ->
                pendingWorkoutDuration = durationMinutes
                editWorkoutViewModel.reset()
                workoutOverviewViewModel.reset()
                navigateTo(WorkoutOverviewHash)
            }
            onNavigateToPayment = { navigateTo(paymentNavigationTarget()) }
            onNavigateToContactUs = { navigateTo(ContactUsHash) }
        }
        isLoggedIn && route == TestExerciseHash -> TestExercisePage {
            viewModel = testExerciseViewModel
            appViewModel = sharedAppViewModel
            onNavigateToDashboard = { navigateTo("") }
            onNavigateToPayment = { navigateTo(paymentNavigationTarget()) }
            onNavigateToContactUs = { navigateTo(ContactUsHash) }
        }
        isLoggedIn && route == WorkoutOverviewHash -> WorkoutOverviewPage {
            viewModel = workoutOverviewViewModel
            appViewModel = sharedAppViewModel
            workoutGuid = pendingWorkoutGuid
            durationInMinutes = pendingWorkoutDuration
            isCompleted = pendingWorkoutIsCompleted
            onStartWorkout = { exerciseNumber ->
                pendingStartExerciseNumber = exerciseNumber
                workoutScreenViewModel.reset()
                navigateTo(WorkoutSessionHash)
            }
            onCompleteWorkout = { navigateToDashboardReloading() }
            onEditWorkout = { editWorkoutViewModel.reset(); navigateTo(EditWorkoutHash) }
            onNavigateToDashboard = { navigateTo("") }
            onNavigateToPayment = { navigateTo(paymentNavigationTarget()) }
            onNavigateToContactUs = { navigateTo(ContactUsHash) }
        }
        isLoggedIn && route == ContactUsHash -> ContactUsPage {
            appViewModel = sharedAppViewModel
            onNavigateToDashboard = { navigateTo("") }
            onNavigateToPayment = { navigateTo(paymentNavigationTarget()) }
            onNavigateToContactUs = { navigateTo(ContactUsHash) }
        }
        isLoggedIn -> DashboardPage {
            viewModel = dashboardViewModel
            appViewModel = sharedAppViewModel
            onAddNewTraining = {
                addNewTrainingViewModel.reset()
                val hasPrevious = dashboardViewModel.dashboardData.value?.hasPreviousProgram ?: false
                navigateTo(if (hasPrevious) AddTrainingExistingHash else AddTraining1Hash)
            }
            onNavigateToInsights = { navigateTo(InsightsHash) }
            onNavigateToPayment = { navigateTo(paymentNavigationTarget()) }
            onNavigateToContactUs = { navigateTo(ContactUsHash) }
            onNavigateToWorkout = ::navigateToWorkout
            onNavigateToUpdateTraining = { updateTrainingViewModel.reset(); navigateTo(UpdateTrainingHash) }
            onNavigateToTest = { navigateTo(TestExerciseHash) }
        }
        route == EmailSignInHash -> EmailSignInPage {
            viewModel = emailPasswordSignInViewModel
            appViewModel = sharedAppViewModel
            onNavigateToCreateAccount = { navigateTo(CreateAccountHash) }
            onNavigateToForgotPassword = { navigateTo(ForgotPasswordHash) }
            onNavigateToVerifyEmail = { email -> pendingEmail = email; navigateTo(VerifyEmailHash) }
        }
        route == CreateAccountHash -> CreateAccountPage {
            viewModel = createAccountViewModel
            onNavigateToVerifyEmail = { email -> pendingEmail = email; navigateTo(VerifyEmailHash) }
        }
        route == ForgotPasswordHash -> ForgotPasswordPage {
            viewModel = forgotPasswordViewModel
            onNavigateToConfirmation = { email -> pendingEmail = email; navigateTo(ResetConfirmationHash) }
        }
        route == VerifyEmailHash -> VerifyEmailPage {
            email = pendingEmail
            onGoToLogin = { navigateTo("") }
        }
        route == ResetConfirmationHash -> ResetPasswordConfirmationPage {
            email = pendingEmail
            onGoToLogin = { navigateTo("") }
        }
        else -> LoginPage {
            viewModel = loginActionsViewModel
            appViewModel = sharedAppViewModel
            onNavigateToEmailSignIn = { navigateTo(EmailSignInHash) }
        }
    }
}
